import type { ErrorInfo} from 'react';
import { useCallback } from 'react';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import { getSentry } from '@/shared/lib/sentry';
import { logger } from '@/shared/utils/logger';
import { ErrorFallback } from './ErrorFallback';
import type {
  ErrorBoundaryProps,
  ErrorCategory,
  ErrorMetadata,
  ErrorRecoveryStrategy,
} from './types';

/**
 * Categorizes errors based on message content for better error handling
 */
const categorizeError = (error: Error): ErrorCategory => {
  const message = error.message.toLowerCase();

  if (
    message.includes('network') ||
    message.includes('fetch') ||
    message.includes('failed to fetch')
  ) {
    return 'network';
  }
  if (message.includes('unauthorized') || message.includes('401')) {
    return 'auth';
  }
  if (
    message.includes('permission') ||
    message.includes('forbidden') ||
    message.includes('403')
  ) {
    return 'permission';
  }
  if (
    message.includes('chunk') ||
    message.includes('loading css chunk') ||
    message.includes('dynamically imported module') ||
    message.includes('importing a module script failed')
  ) {
    return 'chunk-load';
  }
  if (
    message.includes('memory') ||
    message.includes('maximum call stack') ||
    message.includes('out of memory')
  ) {
    return 'memory';
  }
  if (message.includes('validation') || message.includes('invalid')) {
    return 'validation';
  }
  if (message.includes('timeout') || message.includes('timed out')) {
    return 'timeout';
  }
  if (
    message.includes('429') ||
    message.includes('rate limit') ||
    message.includes('too many requests')
  ) {
    return 'rate-limit';
  }
  if (message.includes('500') || message.includes('internal server error')) {
    return 'server-error';
  }

  return 'unknown';
};

/**
 * Generates a unique error ID for tracking
 */
const generateErrorId = (): string => {
  return `error-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
};

/**
 * Default recovery strategy based on error category
 */
const getDefaultRecoveryStrategy = (
  category: ErrorCategory,
): ErrorRecoveryStrategy => {
  switch (category) {
    case 'network':
      return { canRetry: true, shouldClearCache: false };
    case 'auth':
      return { canRetry: true, shouldClearAuth: true };
    case 'chunk-load':
      return { canRetry: true, shouldReloadPage: true };
    case 'memory':
      return { canRetry: false, shouldReloadPage: true };
    case 'permission':
      return { canRetry: false };
    case 'timeout':
      return { canRetry: true, shouldClearCache: false };
    case 'rate-limit':
      return { canRetry: true, shouldClearCache: false }; // Uses exponential backoff
    case 'server-error':
      return { canRetry: true, shouldClearCache: false };
    default:
      return { canRetry: true };
  }
};

export const ErrorBoundary = ({
  children,
  onReset,
  fallbackRender,
  errorLogger,
  enableSentry = true,
}: ErrorBoundaryProps) => {
  const handleReset = useCallback(() => {
    // Custom reset logic provided by parent
    if (onReset) {
      onReset();
    }
    // Note: Page reload should be handled by ErrorFallback based on error category
    // We don't automatically reload here to give users control
  }, [onReset]);

  const handleError = useCallback(
    (error: Error, info: ErrorInfo) => {
      const errorCategory = categorizeError(error);
      const errorId = generateErrorId();
      // Persist generated ID on the error instance so fallback UI can display the same ID.
      (error as Error & { __errorId?: string }).__errorId = errorId;

      // Build error metadata
      const errorMetadata: ErrorMetadata = {
        id: errorId,
        category: errorCategory,
        message: error.message,
        stack: error.stack || undefined,
        componentStack: info.componentStack || undefined,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
      };

      // Log to console in development
      if (import.meta.env.DEV) {
        logger.error('Error caught by ErrorBoundary', errorMetadata);
      }

      // Use custom error logger if provided
      if (errorLogger) {
        errorLogger.log(error, errorMetadata);
      }

      // Send to Sentry with additional context
      const Sentry = getSentry();
      if (enableSentry && Sentry) {
        Sentry.withScope(scope => {
          scope.setTag('error-boundary', true);
          scope.setTag('error-category', errorCategory);
          scope.setTag('error-id', errorId);
          scope.setContext('error-metadata', {
            id: errorMetadata.id,
            category: errorMetadata.category,
            message: errorMetadata.message,
            timestamp: errorMetadata.timestamp,
            userAgent: errorMetadata.userAgent,
            url: errorMetadata.url,
          });
          scope.setLevel('error');
          Sentry.captureException(error);
        });
      }
    },
    [errorLogger, enableSentry],
  );

  // Use custom fallback or default
  const fallbackComponent = fallbackRender || ErrorFallback;

  return (
    <ReactErrorBoundary
      FallbackComponent={fallbackComponent}
      onReset={handleReset}
      onError={handleError}
    >
      {children}
    </ReactErrorBoundary>
  );
};

// Export helper functions for use in fallback or other components
export { categorizeError, generateErrorId, getDefaultRecoveryStrategy };
